/**
 * "Analysis ▾" dropdown — lists precomputed backtest study results for the
 * current symbol and loads one onto the chart. Selecting a result fetches its
 * drawing set and hands it to the existing `loadDrawings` pipeline, so the
 * swings render as locked (read-only) fibs with zero new render code.
 *
 * Note: loading a result REPLACES the current chart drawings (same as the
 * Drawings dialog's Load). "Clear overlay" empties them again.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useAppDispatch } from "@/state/hooks";
import { loadDrawings, clearAllDrawings } from "@/state/slices/uiSlice";
import { api } from "@/api/client";
import type { BacktestResultInfo } from "@/api/types";
import styles from "./BacktestMenu.module.css";

export function BacktestMenu({ tabId, symbol }: { tabId: string; symbol: string }) {
  const dispatch = useAppDispatch();
  const wrapRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState<BacktestResultInfo[]>([]);
  const [status, setStatus] = useState<"idle" | "loading" | "error">("idle");
  const [active, setActive] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setStatus("loading");
    try {
      const r = await api.listBacktests(symbol);
      setResults(r.results);
      setStatus("idle");
    } catch {
      setStatus("error");
    }
  }, [symbol]);

  // Fetch when opened; reset the active marker when the symbol changes.
  useEffect(() => {
    if (open) void refresh();
  }, [open, refresh]);
  useEffect(() => {
    setActive(null);
  }, [symbol]);

  // Close on outside-click / Esc, matching MoreActionsMenu.
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const loadResult = async (name: string) => {
    try {
      const o = await api.getBacktestOverlay(symbol, name);
      dispatch(loadDrawings({ tabId, ...o.set }));
      setActive(name);
      setOpen(false);
    } catch {
      setStatus("error");
    }
  };

  const clearOverlay = () => {
    dispatch(clearAllDrawings({ tabId }));
    setActive(null);
    setOpen(false);
  };

  return (
    <div className={styles.wrap} ref={wrapRef}>
      <button
        type="button"
        className={`${styles.trigger} ${open ? styles.triggerOpen : ""}`}
        onClick={() => setOpen((v) => !v)}
        title="View backtest study results on the chart"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        Analysis
        <span className={styles.caret}>▾</span>
      </button>
      {open ? (
        <div className={styles.menu} role="menu">
          <div className={styles.menuHead}>Backtest results · {symbol}</div>
          <div className={styles.list}>
            {status === "loading" ? (
              <div className={styles.note}>Loading…</div>
            ) : status === "error" ? (
              <div className={styles.note}>Failed to load</div>
            ) : results.length === 0 ? (
              <div className={styles.note}>No results for {symbol}</div>
            ) : (
              results.map((r) => (
                <button
                  key={r.name}
                  type="button"
                  className={`${styles.item} ${active === r.name ? styles.itemActive : ""}`}
                  onClick={() => void loadResult(r.name)}
                  title={r.generated_at ? `Generated ${r.generated_at}` : undefined}
                >
                  <span className={styles.itemName}>{r.name}</span>
                  <span className={styles.itemMeta}>
                    {r.timeframe} · {r.count}
                  </span>
                </button>
              ))
            )}
          </div>
          <div className={styles.divider} />
          <button type="button" className={styles.item} onClick={clearOverlay}>
            Clear overlay
          </button>
        </div>
      ) : null}
    </div>
  );
}
